import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/bits";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const ROLES_ORDER = ["superadmin", "admin", "secretaria", "contabilidad", "maestro", "padre"];
const ROLE_LABEL = {
  superadmin: "Superadministrador",
  admin: "Administrador",
  secretaria: "Secretaría",
  contabilidad: "Contabilidad",
  maestro: "Maestro",
  padre: "Padre / Tutor",
};

const MANUAL = {
  superadmin: {
    intro: "Eres el dueño de la plataforma: gestionas los colegios, sus suscripciones y la configuración global.",
    features: [
      {
        img: "/manual/dashboard.jpeg",
        title: "Colegios",
        desc: "Crea, edita, activa o desactiva colegios. Con 'Entrar' operas dentro de un colegio como si fueras su administrador, sin mezclar datos entre colegios.",
        steps: ["Colegios → Nuevo colegio", "Completa nombre, RNC y contacto", "Usa 'Entrar' para operar dentro del colegio"],
      },
      {
        img: null,
        title: "Suscripciones y facturación de plataforma",
        desc: "Define planes con precio por estudiante (mensual o anual) y genera la facturación mensual a cada colegio activo según su cantidad de estudiantes.",
        steps: ["Suscripciones → crear plan", "Facturación → Generar período", "Registrar pago cuando el colegio pague"],
      },
      {
        img: "/manual/classroom.jpeg",
        title: "Mi Empresa: branding e integraciones",
        desc: "Configura el logo, portada del login, datos fiscales y las credenciales de Google Classroom (Client ID y Secret) para habilitar la conexión de los maestros.",
        steps: ["Mi Empresa → Identidad visual", "Mi Empresa → Integración Google Classroom", "Guardar credenciales"],
      },
    ],
  },
  admin: {
    intro: "Gestionas toda la operación de tu colegio: estudiantes, personal, finanzas, configuración y reportes.",
    features: [
      {
        img: "/manual/dashboard.jpeg",
        title: "Dashboard",
        desc: "Resumen en vivo: estudiantes activos, facturas pendientes, ingresos, gastos, balance y monto por cobrar.",
      },
      {
        img: "/manual/estudiantes.jpeg",
        title: "Estudiantes",
        desc: "Registra estudiantes con su tutor en un solo paso, súbeles foto, importa cientos por Excel con la plantilla descargable, y abre la ficha 360° (notas, tareas, facturas, balance) haciendo clic en la fila.",
        steps: ["Estudiantes → Registrar estudiante", "O Importar Excel para carga masiva", "Clic en la fila → ficha completa"],
      },
      {
        img: "/manual/factura_consolidada.jpeg",
        title: "Facturación (individual y consolidada)",
        desc: "Emite facturas comerciales o fiscales (NCF B02 + ITBIS). Con el switch 'Factura consolidada por tutor' facturas varios hijos en una sola factura a nombre del tutor, etiquetando cada concepto por estudiante. Envía por correo, WhatsApp o descarga el PDF con tu logo.",
        steps: ["Facturas → Nueva factura", "Activa 'consolidada por tutor' si son varios hermanos", "Busca al tutor, marca los estudiantes y emite"],
      },
      {
        img: "/manual/nomina.jpeg",
        title: "Nómina y ponches",
        desc: "Registra empleados con salario y horario personalizado. Cada uno recibe un link único de ponche (cópialo y compártelo) para marcar entrada y salida desde su teléfono. Genera la nómina mensual o quincenal con días trabajados y tardanzas calculadas automáticamente.",
        steps: ["Nómina → Nuevo empleado", "Copiar link de ponche y enviarlo al empleado", "Nómina → Generar nómina del período"],
      },
      {
        img: "/manual/planificacion.jpeg",
        title: "Planificaciones (revisión y aprobación)",
        desc: "Revisa las planificaciones que los maestros importan con IA: aprueba o rechaza con observaciones. El docente ve tus notas y puede corregir.",
      },
      {
        img: null,
        title: "Configuración, usuarios y permisos",
        desc: "En Configuración defines datos fiscales, logo y el QR de registro de padres. En Usuarios creas cuentas para tu personal. En Permisos ajustas qué puede hacer cada rol (secretaría, contabilidad, maestros).",
      },
    ],
  },
  secretaria: {
    intro: "Apoyas la operación diaria: registro de estudiantes, tutores, facturación y agenda escolar.",
    features: [
      {
        img: "/manual/estudiantes.jpeg",
        title: "Estudiantes y tutores",
        desc: "Registra estudiantes nuevos con su padre/madre/tutor, actualiza datos y consulta la ficha completa de cada estudiante con un clic.",
      },
      {
        img: "/manual/facturas.jpeg",
        title: "Facturas y pagos",
        desc: "Emite facturas, registra pagos recibidos en caja y descarga o reenvía comprobantes por correo o WhatsApp.",
      },
      {
        img: null,
        title: "Horarios, menú y documentos",
        desc: "Organiza los horarios en el calendario semanal (arrastra para mover), publica el menú del comedor y gestiona contratos y documentos de los estudiantes.",
      },
    ],
  },
  contabilidad: {
    intro: "Controlas el dinero del colegio: cobros, gastos, inventario y cierres de caja.",
    features: [
      {
        img: "/manual/facturas.jpeg",
        title: "Facturas y pagos",
        desc: "Emites facturas fiscales y comerciales, y apruebas o rechazas los pagos que los padres reportan con comprobante desde su portal.",
      },
      {
        img: null,
        title: "Gastos, inventario y cierre de caja",
        desc: "Registra gastos por categoría, controla el stock de uniformes y libros, y cierra la caja cada día: el sistema calcula ingresos menos gastos con desglose por método de pago.",
      },
      {
        img: "/manual/nomina.jpeg",
        title: "Nómina",
        desc: "Genera la nómina mensual o quincenal con base en los ponches de los empleados, aplica deducciones y marca como pagada.",
      },
    ],
  },
  maestro: {
    intro: "Tu portal tiene tus materias, tus estudiantes, tareas, calificaciones y herramientas de IA para ahorrarte trabajo.",
    features: [
      {
        img: "/manual/portal_maestro.jpeg",
        title: "Mi Portal: tareas y calificaciones",
        desc: "Crea tareas con fecha de entrega y publica calificaciones por período. Solo ves las materias y cursos que tienes asignados.",
        steps: ["Mi Portal → Nueva tarea", "Pestaña Calificaciones → Publicar nota"],
      },
      {
        img: "/manual/comentario_ia.jpeg",
        title: "Comentarios de boleta con IA",
        desc: "Al publicar una nota, el botón 'Generar con IA' redacta automáticamente un comentario profesional para la boleta según las calificaciones del estudiante. Puedes elegir ChatGPT, Claude o Gemini y editar el texto antes de publicar.",
        steps: ["Publicar nota → selecciona estudiante y materia", "Clic en 'Generar con IA'", "Edita si quieres y publica"],
      },
      {
        img: "/manual/planificacion.jpeg",
        title: "Importar planificación con IA",
        desc: "Sube una foto de tu planificación escrita a mano, un PDF escaneado o un Word: la IA la lee, la interpreta y la organiza en planificación anual, unidades didácticas y clases diarias. Revisa el resultado junto al documento original, corrige lo necesario y envíala a revisión del coordinador.",
        steps: ["Planificaciones → Importar planificación", "Sube tu archivo y elige el motor de IA", "Revisa, ajusta y 'Enviar a revisión'"],
      },
      {
        img: "/manual/classroom.jpeg",
        title: "Google Classroom",
        desc: "Conecta tu cuenta de Google para sincronizar tus clases, tareas, materiales y anuncios sin hacer el trabajo dos veces. Con 'Importar tareas a EduNexa' las tareas de Classroom pasan directo a tu portal.",
      },
      {
        img: "/manual/asistente_ia.jpeg",
        title: "Asistente IA",
        desc: "El botón flotante 'IA' está disponible en todas las pantallas: pídele redactar comunicados, ideas de actividades, estrategias pedagógicas o planes de clase. Puedes alternar entre ChatGPT, Claude y Gemini.",
      },
    ],
  },
  padre: {
    intro: "Desde tu portal ves a tus hijos, sus facturas, tareas, calificaciones y el menú del comedor.",
    features: [
      {
        img: "/manual/portal_padre.jpeg",
        title: "Mi Portal",
        desc: "Resumen de tus hijos: facturas pendientes, pagos completados y tareas activas. En las pestañas ves calificaciones publicadas, actividades, menú del comedor y cuentas bancarias del colegio.",
      },
      {
        img: "/manual/portal_padre.jpeg",
        title: "Pagar una factura",
        desc: "En Facturas, usa el botón 'Pagar': sube la foto del comprobante de depósito/transferencia con la referencia, y contabilidad la aprueba. También puedes descargar el PDF de cada factura.",
        steps: ["Facturas → Pagar", "Sube el comprobante y la referencia", "Recibirás confirmación cuando se apruebe"],
      },
      {
        img: "/manual/asistente_ia.jpeg",
        title: "Asistente IA del colegio",
        desc: "El botón flotante 'IA' responde tus preguntas generales sobre pagos, tareas y vida escolar, a cualquier hora.",
      },
      {
        img: "/manual/login.jpeg",
        title: "¿Primera vez?",
        desc: "Regístrate escaneando el código QR que te comparte el colegio o con el enlace de registro. Crea tu cuenta con tu correo y quedarás vinculado a tus hijos.",
      },
    ],
  },
};

function FeatureCard({ f, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={testid}>
      {f.img && (
        <a href={f.img} target="_blank" rel="noreferrer" title="Ver imagen completa">
          <img src={f.img} alt={f.title} className="w-full aspect-video object-cover object-top border-b border-slate-100 hover:opacity-90 transition-opacity" loading="lazy" />
        </a>
      )}
      <div className="p-5">
        <h3 className="text-base font-semibold text-slate-900">{f.title}</h3>
        <p className="text-sm text-slate-600 leading-relaxed mt-1.5">{f.desc}</p>
        {f.steps && (
          <ol className="mt-3 space-y-1">
            {f.steps.map((s, i) => (
              <li key={i} className="flex gap-2 text-xs text-slate-500">
                <span className="w-4 h-4 rounded-full bg-blue-800/10 text-blue-800 flex items-center justify-center font-semibold shrink-0 mt-px">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

export default function Manual() {
  const { user } = useAuth();
  const defaultTab = ROLES_ORDER.includes(user?.role) ? user.role : "admin";
  const [tab, setTab] = useState(defaultTab);
  const section = MANUAL[tab];

  return (
    <div data-testid="manual-page">
      <PageHeader title="Manual de Uso" subtitle="Guía paso a paso con imágenes para cada tipo de usuario de la plataforma" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 mb-5" data-testid="manual-tabs">
          {ROLES_ORDER.map((r) => (
            <TabsTrigger key={r} value={r} data-testid={`manual-tab-${r}`}>{ROLE_LABEL[r]}</TabsTrigger>
          ))}
        </TabsList>
        {ROLES_ORDER.map((r) => (
          <TabsContent key={r} value={r}>
            <p className="text-sm text-slate-500 mb-5 max-w-3xl" data-testid={`manual-intro-${r}`}>{MANUAL[r].intro}</p>
            <div className="grid md:grid-cols-2 gap-5">
              {MANUAL[r].features.map((f, i) => (
                <FeatureCard key={i} f={f} testid={`manual-card-${r}-${i}`} />
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
